<template>
  <div>
    <a-layout class="top-layout">
      <slot name="flash" />

      <a-layout-content class="login-page__content">
        <div class="login-page__columns">
          <div v-if="!isTidra" class="value-blurb">
            <h1 class="text-gradient">
              Build and ship high-quality software, faster
            </h1>
            <div class="value-props">
              <h2>
                <GradientSearchIcon />
                Get visibility into your entire software ecosystem
              </h2>
              <div class="icon-gradient vertical-divider" />
              <h2>
                <GradientCheckIcon />
                Make standards and compliance easy with built-in guardrails
              </h2>
              <div class="icon-gradient vertical-divider" />
              <h2>
                <GradientSettingIcon />
                Unblock bottlenecks with<br />self-service tools
              </h2>
            </div>
          </div>
          <div v-else class="value-blurb tidra-value-blurb">
            <h1 class="tidra-headline">
              <span class="tidra-headline-line"
                >Reclaim Engineering&nbsp;Hours.</span
              >
              <span class="tidra-headline-gradient">
                Automate Maintenance in&nbsp;minutes.
              </span>
            </h1>
            <p class="tidra-tagline">Your repos, your rules, your timeline.</p>
            <div class="tidra-value-props">
              <div
                v-for="item in tidraValueProps"
                :key="item.title"
                class="tidra-value-prop"
              >
                <div class="tidra-check-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M2 8l4 4 8-8" />
                  </svg>
                </div>
                <div>
                  <p class="tidra-value-prop-title">{{ item.title }}</p>
                  <p class="tidra-value-prop-description">
                    {{ item.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="content-wrapper">
            <a-card
              class="content-card"
              :bordered="true"
              :body-style="contentCardBodyStyle"
            >
              <BrandLogo type="full" color="primary" class="logo" />

              <slot name="content">
                <div></div>
              </slot>
            </a-card>
          </div>
        </div>
      </a-layout-content>

      <div class="expando" />
      <CopyrightFooter />
    </a-layout>
  </div>
</template>

<script>
import CopyrightFooter from "./CopyrightFooter.vue";
import BrandLogo from "@/components/BrandLogo.vue";
import {
  GradientSearchIcon,
  GradientSettingIcon,
  GradientCheckIcon,
} from "@/shared/icons.js";
import "./tidra-login-marketing.css";

const TIDRA_VALUE_PROPS = [
  {
    title: "From zero to PRs in minutes",
    description:
      "Connect your Git provider, describe what needs to change, and Tidra generates pull requests across every repo.",
  },
  {
    title: "Your code stays yours",
    description:
      "Read-only access, no training on your data, SOC 2 compliant. Every PR requires your review before merge.",
  },
  {
    title: "Works at any scale",
    description:
      "Whether you have 10 repos or 10,000. Dependency upgrades, migrations, and code standard enforcement, all handled.",
  },
];

export default {
  components: {
    CopyrightFooter,
    BrandLogo,
    GradientSearchIcon,
    GradientSettingIcon,
    GradientCheckIcon,
  },

  inject: {
    brand: { default: "opslevel" },
  },

  data() {
    return {
      tidraValueProps: TIDRA_VALUE_PROPS,
    };
  },

  computed: {
    isTidra() {
      return this.brand === "tidra";
    },
    contentCardBodyStyle() {
      return {
        padding: "40px 60px 60px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "#fff",
      };
    },
  },
};
</script>

<style scoped>
.value-blurb {
  font-style: normal;
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  max-width: 720px;
}
.value-blurb:not(.tidra-value-blurb) h1 {
  font-size: 48px;
  font-weight: 700;
  line-height: 59px;
  margin-bottom: 48px;
}

.value-blurb:not(.tidra-value-blurb) h2 {
  color: #595959;
  font-size: 26px;
  line-height: 37px;
  font-weight: 300;
  margin-bottom: 0;
  max-width: 440px;
  display: flex;
  letter-spacing: 0.5px;
}

.value-props :deep(.anticon) {
  font-size: 44px;
}

.value-props {
  margin-left: 32px;
}

.value-blurb i {
  margin-right: 16px;
}

.vertical-divider {
  height: 42px;
  width: 3px;
  margin: -16px 0 16px 20px; /* align with icons, ignoring text wrapping */
}

.login-page__content {
  display: flex;
  flex: 1 0 auto;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  margin: 72px 0;
  padding: 0 72px;
  box-sizing: border-box;
  overflow: visible;
}

.login-page__columns {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 64px;
  width: 100%;
  max-width: 1280px;
}

.content-wrapper {
  flex: 0 0 auto;
  width: auto;
}

.top-layout > .expando {
  display: none;
}
.text-gradient {
  filter: linear-gradient(90deg, #347ed5 -5.35%, #05d1c6 104.49%);
  background: linear-gradient(90deg, #347ed5 -5.35%, #05d1c6 104.49%);
  -webkit-text-fill-color: transparent;
  background-clip: text;
  -webkit-background-clip: text;
}
.text-gradient::selection {
  -webkit-text-fill-color: white;
}
.icon-gradient {
  background: linear-gradient(180deg, #347ed5 0%, #05d1c6 100%);
}

/* Override global .content-card padding; layout lives on ant-card-body */
.content-card {
  width: auto;
  min-width: 320px;
  max-width: 420px;
  padding: 0;
  background: #fff;
}

.content-card :deep(.ant-card-body) {
  background: #fff;
}

.content-card :deep(.ant-card-head) {
  display: none;
}

.logo {
  margin: auto;
  width: 150px;
  margin-bottom: 48px;
  margin-top: 16px;
}

@media (max-width: 1112px) {
  .login-page__columns {
    flex-direction: column;
    align-items: center;
    gap: 32px;
  }

  .value-blurb.tidra-value-blurb {
    flex: 0 1 auto;
    text-align: center;
  }

  .content-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
  }
}

@media (max-width: 1020px) {
  .value-blurb:not(.tidra-value-blurb) {
    display: none;
  }

  .login-page__content {
    margin: 32px 0;
    padding: 0 24px;
  }

  .login-page__columns {
    gap: 32px;
  }
}
</style>
